import { doc, getDoc } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { useEffect, useMemo, useRef, useState } from 'react'
import { firebaseFunctions, firestoreDb } from '../../services/firebase'
import { processExperienceEventSync, queuePassiveExperienceEvent } from '../../services/experienceGamificationService'
import type { CarouselSwipeEffect } from './CarouselVariants'
import {
  getDefaultCarouselCache,
  useCarouselCache,
  type CarouselMediaCache,
} from './CarouselCacheContext'

export type CarouselUiMode = 'fullscreen' | 'dialog'
export type CarouselMediaType = 'image' | 'video'

export type CarouselSlide = {
  id: string
  assetPath: string
  type: CarouselMediaType
  dwellMs: number
  swipeEffect: CarouselSwipeEffect
  loopVideo: boolean
}

export type CarouselSettings = {
  defaultTransition: CarouselSwipeEffect
  dwellMs: number
  uiMode: CarouselUiMode
  showProgress: boolean
  dismissible: boolean
}

export type CarouselConfig = {
  journeyId: string
  autoLoop: boolean
  settings: CarouselSettings
  slides: CarouselSlide[]
}

export type ReadyCarouselSlide = CarouselSlide & { assetUrl: string }
export type ReadyCarouselConfig = Omit<CarouselConfig, 'slides'> & { slides: ReadyCarouselSlide[] }

export type CarouselAuditContext = {
  workspaceId: string
  actorId?: string
  actorName?: string
}

type RegistryState = {
  journeyId: string
  config: ReadyCarouselConfig | null
  loading: boolean
  error: Error | null
}

const effects = new Set<CarouselSwipeEffect>([
  'fluid-parallax',
  'glass-morph-fold',
  'kinetic-snap',
  'monochrome-fade',
])
const uiModes = new Set<CarouselUiMode>(['fullscreen', 'dialog'])
const mediaTypes = new Set<CarouselMediaType>(['image', 'video'])
const preloadTimeoutMs = 30_000
// Server signs URLs for 10 minutes; cache 8 to give a safety margin and
// guarantee the in-flight playback never trips an expired URL.
const signedUrlCacheMs = 8 * 60 * 1000

const isRecord = (value: unknown): value is Record<string, unknown> => Boolean(value) && typeof value === 'object' && !Array.isArray(value)
const isPositiveDuration = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value) && value > 0
const hasExactKeys = (value: Record<string, unknown>, keys: string[]) => {
  const actual = Object.keys(value).sort()
  const expected = [...keys].sort()
  return actual.length === expected.length && actual.every((key, index) => key === expected[index])
}

const invalidConfig = (detail: string) => new Error(`Invalid carousel configuration: ${detail}`)

export const parseCarouselConfig = (value: unknown, documentId: string): CarouselConfig => {
  if (!isRecord(value) || !hasExactKeys(value, ['journeyId', 'autoLoop', 'settings', 'slides'])) throw invalidConfig('root schema mismatch')
  if (typeof value.journeyId !== 'string' || !value.journeyId || value.journeyId !== documentId) throw invalidConfig('journeyId must match the document ID')
  if (typeof value.autoLoop !== 'boolean') throw invalidConfig('autoLoop must be boolean')
  if (!isRecord(value.settings) || !hasExactKeys(value.settings, ['defaultTransition', 'dwellMs', 'uiMode', 'showProgress', 'dismissible'])) {
    throw invalidConfig('settings schema mismatch')
  }
  const settings = value.settings
  if (typeof settings.defaultTransition !== 'string' || !effects.has(settings.defaultTransition as CarouselSwipeEffect)) throw invalidConfig('unsupported defaultTransition')
  if (!isPositiveDuration(settings.dwellMs)) throw invalidConfig('settings.dwellMs must be a positive number')
  if (typeof settings.uiMode !== 'string' || !uiModes.has(settings.uiMode as CarouselUiMode)) throw invalidConfig('unsupported uiMode')
  if (typeof settings.showProgress !== 'boolean' || typeof settings.dismissible !== 'boolean') throw invalidConfig('settings flags must be boolean')
  if (!Array.isArray(value.slides) || value.slides.length === 0) throw invalidConfig('slides must be a non-empty array')

  const seenIds = new Set<string>()
  const slides = value.slides.map((candidate, index): CarouselSlide => {
    if (!isRecord(candidate) || !hasExactKeys(candidate, ['id', 'assetPath', 'type', 'dwellMs', 'swipeEffect', 'loopVideo'])) throw invalidConfig(`slide ${index} schema mismatch`)
    if (typeof candidate.id !== 'string' || !candidate.id || seenIds.has(candidate.id)) throw invalidConfig(`slide ${index} has an invalid or duplicate id`)
    seenIds.add(candidate.id)
    if (typeof candidate.assetPath !== 'string' || !candidate.assetPath.startsWith('gs://')) throw invalidConfig(`slide ${candidate.id} assetPath must be a gs:// path`)
    if (typeof candidate.type !== 'string' || !mediaTypes.has(candidate.type as CarouselMediaType)) throw invalidConfig(`slide ${candidate.id} has an unsupported type`)
    if (!isPositiveDuration(candidate.dwellMs)) throw invalidConfig(`slide ${candidate.id} dwellMs must be a positive number`)
    if (typeof candidate.swipeEffect !== 'string' || !effects.has(candidate.swipeEffect as CarouselSwipeEffect)) throw invalidConfig(`slide ${candidate.id} has an unsupported swipeEffect`)
    if (typeof candidate.loopVideo !== 'boolean') throw invalidConfig(`slide ${candidate.id} loopVideo must be boolean`)
    return candidate as CarouselSlide
  })

  return {
    journeyId: value.journeyId,
    autoLoop: value.autoLoop,
    settings: settings as CarouselSettings,
    slides,
  }
}

const preloadImage = (url: string) => new Promise<void>((resolve, reject) => {
  const image = new Image()
  const timeout = window.setTimeout(() => reject(new Error('Image preload timed out')), preloadTimeoutMs)
  const finish = (callback: () => void) => {
    window.clearTimeout(timeout)
    callback()
  }
  image.decoding = 'async'
  image.onload = () => {
    const decoded = image.decode?.()
    if (decoded) void decoded.then(() => finish(resolve), (error) => finish(() => reject(error)))
    else finish(resolve)
  }
  image.onerror = () => finish(() => reject(new Error('Image failed to load')))
  image.src = url
})

const preloadVideo = (url: string) => new Promise<void>((resolve, reject) => {
  const video = document.createElement('video')
  let canPlayThrough = false
  let fiveSecondsBuffered = false
  let settled = false
  const timeout = window.setTimeout(() => {
    if (settled) return
    settled = true
    cleanup()
    reject(new Error('Video preload timed out'))
  }, preloadTimeoutMs)

  const cleanup = () => {
    window.clearTimeout(timeout)
    video.removeEventListener('canplaythrough', onCanPlayThrough)
    video.removeEventListener('progress', onProgress)
    video.removeEventListener('loadedmetadata', onProgress)
    video.removeEventListener('error', onError)
    video.removeAttribute('src')
    video.load()
  }
  const finish = () => {
    if (settled || !canPlayThrough || !fiveSecondsBuffered) return
    settled = true
    cleanup()
    resolve()
  }
  const onProgress = () => {
    const target = Number.isFinite(video.duration) ? Math.min(5, video.duration) : 5
    for (let index = 0; index < video.buffered.length; index += 1) {
      if (video.buffered.start(index) <= 0.1 && video.buffered.end(index) >= target) {
        fiveSecondsBuffered = true
        break
      }
    }
    finish()
  }
  const onCanPlayThrough = () => {
    canPlayThrough = true
    onProgress()
  }
  const onError = () => {
    if (settled) return
    settled = true
    cleanup()
    reject(new Error('Video failed to reach canplaythrough'))
  }

  video.preload = 'auto'
  video.muted = true
  video.playsInline = true
  video.addEventListener('canplaythrough', onCanPlayThrough)
  video.addEventListener('progress', onProgress)
  video.addEventListener('loadedmetadata', onProgress)
  video.addEventListener('error', onError)
  video.src = url
  video.load()
})

/**
 * Fetches just the signed URL for a slide. Used by useCarouselSequence on
 * every slide entry to guarantee the URL is fresh even for long-running or
 * autoLoop carousels where the original config-fetch URLs would expire.
 */
export const signCarouselSlideUrl = async (
  slide: Pick<CarouselSlide, 'id' | 'assetPath'>,
  journeyId: string,
): Promise<string> => {
  const signer = httpsCallable<
    { journeyId: string; slideId: string },
    { assetUrl: string; expiresAt: number }
  >(firebaseFunctions, 'getCarouselAssetUrl')
  const response = await signer({ journeyId, slideId: slide.id })
  return response.data.assetUrl
}

/**
 * Initial preload: signs the URL AND warms the browser media cache by
 * decoding the image / buffering the first few seconds of video. The signed
 * URL is also cached for `signedUrlCacheMs` so concurrent preloads in the
 * same config-fetch don't issue duplicate signer calls.
 */
export const preloadCarouselSlide = async (
  slide: CarouselSlide,
  journeyId: string,
  cache: CarouselMediaCache = getDefaultCarouselCache(),
): Promise<ReadyCarouselSlide> => {
  const cacheKey = `${slide.type}:${slide.assetPath}`
  const existing = cache.get(cacheKey)
  if (existing) return { ...slide, assetUrl: await existing }
  const pending = (async () => {
    const assetUrl = await signCarouselSlideUrl(slide, journeyId)
    if (slide.type === 'image') await preloadImage(assetUrl)
    else await preloadVideo(assetUrl)
    return assetUrl
  })()
  cache.set(cacheKey, pending)
  pending.catch(() => cache.delete(cacheKey))
  void pending.then(() => cache.scheduleEviction(cacheKey, signedUrlCacheMs))
  return { ...slide, assetUrl: await pending }
}

/**
 * Records a per-slide media-load failure through the Experience Engine. The
 * `workspace_activity` rule blocks client writes; this routes through the
 * uid-keyed user_activity + ledger entries the engine maintains.
 */
const reportMediaFailure = async (
  journeyId: string,
  slide: CarouselSlide,
  error: unknown,
  audit?: CarouselAuditContext,
): Promise<void> => {
  console.error(`[CinematicCarousel] Skipping media ${slide.id}`, error)
  const payload = {
    workspaceId: audit?.workspaceId,
    sourceId: slide.id,
    journeyId,
    slideId: slide.id,
    mediaType: slide.type,
    reason: error instanceof Error ? error.message : 'Carousel media unavailable',
    title: audit?.actorName ?? undefined,
  }
  try {
    if (!await queuePassiveExperienceEvent('CAROUSEL_MEDIA_LOAD_FAILED', payload)) {
      await processExperienceEventSync('CAROUSEL_MEDIA_LOAD_FAILED', payload)
    }
  } catch (telemetryError) {
    console.error('[CinematicCarousel] Telemetry write failed', telemetryError)
  }
}

export const fetchCarouselConfig = async (
  journeyId: string,
  audit?: CarouselAuditContext,
  cache: CarouselMediaCache = getDefaultCarouselCache(),
): Promise<ReadyCarouselConfig> => {
  if (!journeyId.trim()) throw new Error('A journeyId is required.')
  const snapshot = await getDoc(doc(firestoreDb, 'carousel_configs', journeyId))
  if (!snapshot.exists()) throw new Error(`Carousel journey "${journeyId}" was not found.`)
  const config = parseCarouselConfig(snapshot.data(), snapshot.id)
  const results = await Promise.allSettled(
    config.slides.map((slide) => preloadCarouselSlide(slide, config.journeyId, cache)),
  )
  const slides: ReadyCarouselSlide[] = []
  for (let index = 0; index < results.length; index += 1) {
    const result = results[index]
    if (result.status === 'fulfilled') slides.push(result.value)
    else await reportMediaFailure(journeyId, config.slides[index], result.reason, audit)
  }
  if (slides.length === 0) throw new Error(`Carousel journey "${journeyId}" has no playable slides.`)
  return { ...config, slides }
}

export const useCarouselRegistry = (journeyId: string, audit?: CarouselAuditContext): RegistryState => {
  const [state, setState] = useState<RegistryState>({ journeyId, config: null, loading: true, error: null })
  const cache = useCarouselCache()
  // Snapshot the audit context once per render with stable identity — the
  // effect downstream depends on the memoized object, not on each field, so
  // an inline `auditContext={{ workspaceId, ... }}` from the parent no longer
  // retriggers a full registry refetch + signer call on every re-render.
  const memoizedAudit = useMemo<CarouselAuditContext | undefined>(() => {
    if (!audit?.workspaceId) return undefined
    return { workspaceId: audit.workspaceId, actorId: audit.actorId, actorName: audit.actorName }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audit?.workspaceId, audit?.actorId, audit?.actorName])
  const auditRef = useRef(memoizedAudit)
  useEffect(() => { auditRef.current = memoizedAudit }, [memoizedAudit])

  useEffect(() => {
    let active = true
    void fetchCarouselConfig(journeyId, auditRef.current, cache)
      .then((config) => { if (active) setState({ journeyId, config, loading: false, error: null }) })
      .catch((error: unknown) => { if (active) setState({ journeyId, config: null, loading: false, error: error instanceof Error ? error : new Error('Carousel loading failed.') }) })
    return () => { active = false }
  }, [cache, journeyId])

  return state.journeyId === journeyId
    ? state
    : { journeyId, config: null, loading: true, error: null }
}
